import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertCircle,
  Database,
  Key,
  Shield,
  Percent,
  Eye,
  EyeOff,
  Copy,
  RefreshCw,
  Trash2,
  Plus,
} from 'lucide-react';
import { fetchB2Projects, fetchB2Stats, fetchB2Project, generateB2, regenerateB2, deleteB2 } from '@/api/admin';
import { toast } from 'sonner';
import type { B2Project } from '@/types/admin';

export function AdminB2Page() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-b2', page, status],
    queryFn: () => fetchB2Projects(page, status || undefined),
  });

  const { data: stats } = useQuery({
    queryKey: ['admin-b2-stats'],
    queryFn: fetchB2Stats,
  });

  const { data: projectDetail, isLoading: detailLoading } = useQuery({
    queryKey: ['admin-b2-project', selectedProject],
    queryFn: () => fetchB2Project(selectedProject!),
    enabled: !!selectedProject && detailDialogOpen,
  });

  const generateMutation = useMutation({
    mutationFn: (projectId: string) => generateB2(projectId),
    onSuccess: () => {
      toast.success('Credentials generated successfully');
      queryClient.invalidateQueries({ queryKey: ['admin-b2'] });
      queryClient.invalidateQueries({ queryKey: ['admin-b2-stats'] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to generate credentials'),
  });

  const regenerateMutation = useMutation({
    mutationFn: (projectId: string) => regenerateB2(projectId),
    onSuccess: () => {
      toast.success('Credentials regenerated successfully');
      queryClient.invalidateQueries({ queryKey: ['admin-b2'] });
      queryClient.invalidateQueries({ queryKey: ['admin-b2-project', selectedProject] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to regenerate credentials'),
  });

  const deleteMutation = useMutation({
    mutationFn: (projectId: string) => deleteB2(projectId),
    onSuccess: () => {
      toast.success('Credentials deleted');
      queryClient.invalidateQueries({ queryKey: ['admin-b2'] });
      queryClient.invalidateQueries({ queryKey: ['admin-b2-stats'] });
      setDetailDialogOpen(false);
    },
    onError: (err: any) => toast.error(err.message || 'Failed to delete credentials'),
  });

  const projects = data?.projects || [];
  const pagination = data?.pagination;

  const openDetailDialog = (projectId: string) => {
    setSelectedProject(projectId);
    setShowCredentials(false);
    setDetailDialogOpen(true);
  };

  const handleGenerate = (projectId: string, projectName: string) => {
    if (confirm(`Generate B2 credentials for "${projectName}"?`)) {
      generateMutation.mutate(projectId);
    }
  };

  const handleRegenerate = (projectId: string, projectName: string) => {
    if (confirm(`Regenerate B2 credentials for "${projectName}"? This will invalidate the existing credentials.`)) {
      regenerateMutation.mutate(projectId);
    }
  };

  const handleDelete = (projectId: string, projectName: string) => {
    if (confirm(`Delete B2 credentials for "${projectName}"? This action cannot be undone.`)) {
      deleteMutation.mutate(projectId);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const coveragePercent = stats?.credential_coverage || 0;

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">B2 Credentials</h1>
          <p className="text-sm text-muted-foreground">Manage Backblaze B2 credentials per project</p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-sm">Filter:</Label>
          <Select value={status || 'any'} onValueChange={(v) => { setStatus(v === 'any' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-52">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All Projects</SelectItem>
              <SelectItem value="has_credentials">Has Credentials</SelectItem>
              <SelectItem value="no_credentials">Missing Credentials</SelectItem>
              <SelectItem value="backup_storage_enabled">Backup Storage Enabled</SelectItem>
              <SelectItem value="backup_storage_disabled">Backup Storage Disabled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Database className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Projects</p>
                <p className="text-2xl font-semibold">{stats?.total_projects || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Key className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Has Credentials</p>
                <p className="text-2xl font-semibold">{stats?.projects_with_credentials || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Shield className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Backup Enabled</p>
                <p className="text-2xl font-semibold">{stats?.projects_with_backup_storage || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Percent className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Coverage</p>
                <p className="text-2xl font-semibold">{coveragePercent}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>Failed to load projects.</AlertDescription>
        </Alert>
      )}

      {/* Projects Table */}
      <Card>
        <CardHeader>
          <CardTitle>Projects ({pagination?.total || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : projects.length ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground border-b">
                      <th className="py-3 px-2 font-medium">Project</th>
                      <th className="py-3 px-2 font-medium">Status</th>
                      <th className="py-3 px-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((project: B2Project) => (
                      <tr key={project.id} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-2">
                          <div>
                            <span className="font-medium">{project.name}</span>
                            <span className="text-xs text-muted-foreground ml-2">#{project.id}</span>
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          <Badge variant="outline" className="text-xs">
                            —
                          </Badge>
                        </td>
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openDetailDialog(String(project.id))}
                              title="View details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleGenerate(String(project.id), project.name)}
                              disabled={generateMutation.isPending}
                              title="Generate credentials"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRegenerate(String(project.id), project.name)}
                              disabled={regenerateMutation.isPending}
                              title="Regenerate credentials"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDelete(String(project.id), project.name)}
                              disabled={deleteMutation.isPending}
                              title="Delete credentials"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pagination && pagination.pages > 1 && (
                <div className="flex items-center justify-between mt-4 text-sm">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <span className="text-muted-foreground">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page >= pagination.pages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No projects found.</p>
          )}
        </CardContent>
      </Card>

      {/* Project Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Project Details</DialogTitle>
            <DialogDescription>
              B2 credentials and backup information
            </DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <p className="text-sm text-muted-foreground py-4">Loading...</p>
          ) : projectDetail ? (
            <div className="space-y-4 py-4">
              {/* Project Info */}
              <div>
                <h4 className="font-medium">{projectDetail.project.name}</h4>
                <p className="text-sm text-muted-foreground">Project #{projectDetail.project.id}</p>
              </div>

              {/* Backup Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-muted p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground">Recent Backups</p>
                  <p className="text-xl font-semibold">{projectDetail.recent_backups}</p>
                </div>
                <div className="bg-muted p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground">Total Backups</p>
                  <p className="text-xl font-semibold">{projectDetail.total_backups}</p>
                </div>
              </div>

              {/* Credentials (if available) */}
              {(projectDetail as any).key_id && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Credentials</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowCredentials(!showCredentials)}
                    >
                      {showCredentials ? (
                        <><EyeOff className="h-4 w-4 mr-1" /> Hide</>
                      ) : (
                        <><Eye className="h-4 w-4 mr-1" /> Show</>
                      )}
                    </Button>
                  </div>

                  <div className="bg-muted p-3 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Key ID:</span>
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-background px-2 py-0.5 rounded">
                          {showCredentials ? (projectDetail as any).key_id : '••••••••••••'}
                        </code>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={() => copyToClipboard((projectDetail as any).key_id, 'Key ID')}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    {(projectDetail as any).bucket_name && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Bucket:</span>
                        <div className="flex items-center gap-2">
                          <code className="text-xs bg-background px-2 py-0.5 rounded">
                            {(projectDetail as any).bucket_name}
                          </code>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={() => copyToClipboard((projectDetail as any).bucket_name, 'Bucket')}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Recent Backups List */}
              {projectDetail.recent_backups_list && projectDetail.recent_backups_list.length > 0 && (
                <div>
                  <Label className="text-sm font-medium mb-2 block">Recent Backups</Label>
                  <div className="bg-muted p-3 rounded-lg max-h-40 overflow-y-auto">
                    <ul className="space-y-1 text-xs">
                      {projectDetail.recent_backups_list.map((backup) => (
                        <li key={backup.id} className="flex items-center justify-between">
                          <span className="text-muted-foreground">#{backup.id}</span>
                          <span>
                            {backup.created_at
                              ? new Date(backup.created_at).toLocaleString()
                              : '—'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
